import { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { ExternalLink, FileSearch, ShieldCheck, X } from "lucide-react";
import { db } from "./firebase";

export type ProvenanceSource = {
  collection?: string;
  id?: string;
  path?: string;
  title: string;
  url?: string;
  date?: string;
  excerpt?: string;
};

export type ProvenanceRecord = {
  title: string;
  statement: string;
  classification: string;
  confidence?: string;
  rationale?: string;
  sources: ProvenanceSource[];
};

export function ProvenanceButton({ onClick, label = "Evidence" }: { onClick: () => void; label?: string }) {
  return <button type="button" className="provenance-button" onClick={onClick}><ShieldCheck size={12} />{label}</button>;
}

export function ProvenanceDrawer({ userId, record, onClose }: { userId: string; record: ProvenanceRecord | null; onClose: () => void }) {
  const internalSources = useMemo(() => record?.sources.filter((source) => !source.url && sourcePath(source).length > 0) ?? [], [record]);
  const [selectedSource, setSelectedSource] = useState<ProvenanceSource | null>(null);
  const [sourceContent, setSourceContent] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSelectedSource(internalSources[0] ?? null);
    setSourceContent("");
  }, [internalSources, record]);

  useEffect(() => {
    if (!record || !selectedSource) return;
    const path = sourcePath(selectedSource);
    if (!path.length) return;
    let active = true;
    setLoading(true);
    getDoc(doc(db, "users", userId, ...path)).then((snapshot) => {
      if (!active) return;
      setSourceContent(snapshot.exists() ? sourcePreview(snapshot.data()) : "The source record is no longer available.");
    }).catch((reason: unknown) => {
      if (active) setSourceContent(reason instanceof Error ? reason.message : "Unable to load this source.");
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [record, selectedSource, userId]);

  if (!record) return null;
  return <div className="provenance-backdrop" role="presentation" onClick={onClose}>
    <aside className="provenance-drawer" role="dialog" aria-modal="true" aria-label="Evidence and provenance" onClick={(event) => event.stopPropagation()}>
      <header><div><span>Evidence and provenance</span><h2>{record.title}</h2></div><button title="Close evidence drawer" onClick={onClose}><X size={17} /></button></header>
      <section className="provenance-statement"><div><span className={`provenance ${record.classification}`}>{record.classification}</span>{record.confidence && <span className={`provenance-confidence ${record.confidence}`}>{record.confidence} confidence</span>}</div><p>{record.statement}</p>{record.rationale && <small>{record.rationale}</small>}</section>
      <section className="provenance-sources"><h3>Sources</h3>{record.sources.length ? record.sources.map((source, index) => source.url
        ? <a key={`${source.title}-${index}`} href={source.url} target="_blank" rel="noreferrer"><ExternalLink size={13} /><span><strong>{source.title || "Public source"}</strong>{source.date && <small>{source.date}</small>}{source.excerpt && <p>{source.excerpt}</p>}</span></a>
        : <button key={`${source.title}-${index}`} className={selectedSource === source ? "active" : ""} onClick={() => setSelectedSource(source)}><FileSearch size={13} /><span><strong>{source.title || source.collection || "Internal source"}</strong><small>{source.path || [source.collection, source.id].filter(Boolean).join("/")}</small>{source.excerpt && <p>{source.excerpt}</p>}</span></button>)
        : <p className="provenance-empty">No supporting source was attached. Treat this as a data gap.</p>}</section>
      {selectedSource && <section className="provenance-preview"><h3>Internal source preview</h3><pre>{loading ? "Loading source..." : sourceContent || selectedSource.excerpt || "No short preview is available."}</pre></section>}
    </aside>
  </div>;
}

function sourcePath(source: ProvenanceSource) {
  if (source.path) return source.path.split("/").filter(Boolean);
  if (!source.collection || !source.id) return [];
  const collection = ({
    memory: "memories",
    brief: "briefs",
    contact: "contacts",
    organisation: "organisations",
    opportunity: "opportunities",
  } as Record<string, string>)[source.collection] || source.collection;
  return [collection, source.id];
}

function sourcePreview(data: Record<string, unknown>) {
  const candidate = data.summary || data.detail || data.markdown || (data.brief as { executiveSummary?: string } | undefined)?.executiveSummary || data.notes || data.answer || data.content;
  if (typeof candidate === "string" && candidate.trim()) return candidate.trim().slice(0, 8000);
  return JSON.stringify(sanitizedSource(data), null, 2).slice(0, 8000);
}

function sanitizedSource(data: Record<string, unknown>) {
  return Object.fromEntries(Object.entries(data).filter(([key, value]) => !["embedding", "openaiResponseId"].includes(key) && typeof value !== "object"));
}
